/**
 * Command palette registry (UI state only). Pages register the commands that
 * make sense where you are while they're mounted, so the palette is always
 * contextual: on a trip you get trip commands, in the Library list commands,
 * and navigation everywhere. `priority` (and recency) decide what floats to
 * the top — e.g. lists currently scrolled into view outrank the rest.
 */
import type { LucideIcon } from 'lucide-react';
import { useEffect } from 'react';
import { create } from 'zustand';

export interface PaletteCommand {
  /** Stable id; also the recency key. */
  id: string;
  label: string;
  /** Section heading in the palette. */
  group: string;
  /** Extra words that should match (list names, synonyms). */
  keywords?: string[];
  /** Shown at the right: a shortcut hint ("E", "⌘K") or a small note. */
  hint?: string;
  icon?: LucideIcon;
  /** 0 = ordinary; higher floats up. Visible-on-screen things get +2 or so. */
  priority?: number;
  run: () => void;
}

interface CommandsState {
  open: boolean;
  setOpen: (open: boolean) => void;
  sources: Record<string, PaletteCommand[]>;
  register: (source: string, commands: PaletteCommand[]) => void;
  unregister: (source: string) => void;
  /** Recently run command ids, newest first (persisted per device). */
  recent: string[];
  markUsed: (id: string) => void;
}

const RECENT_KEY = 'uc-command-recent';
const RECENT_MAX = 12;

function loadRecent(): string[] {
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    return Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === 'string') : [];
  } catch {
    return [];
  }
}

export const useCommandsStore = create<CommandsState>((set, get) => ({
  open: false,
  setOpen: (open) => set({ open }),
  sources: {},
  register: (source, commands) =>
    set((state) => ({ sources: { ...state.sources, [source]: commands } })),
  unregister: (source) =>
    set((state) => {
      const { [source]: _dropped, ...rest } = state.sources;
      return { sources: rest };
    }),
  recent: loadRecent(),
  markUsed: (id) => {
    const recent = [id, ...get().recent.filter((other) => other !== id)].slice(0, RECENT_MAX);
    set({ recent });
    try {
      localStorage.setItem(RECENT_KEY, JSON.stringify(recent));
    } catch {
      // Recency is a convenience.
    }
  },
}));

/**
 * Register a page's commands while it's mounted. Re-register whenever `commands`
 * changes identity — callers memoize, or accept a cheap re-set per render.
 */
export function useRegisterCommands(source: string, commands: PaletteCommand[]): void {
  const register = useCommandsStore((state) => state.register);
  const unregister = useCommandsStore((state) => state.unregister);
  useEffect(() => {
    register(source, commands);
    return () => unregister(source);
  }, [source, commands, register, unregister]);
}

/** Every registered command, flattened. */
export function allCommands(sources: Record<string, PaletteCommand[]>): PaletteCommand[] {
  return Object.values(sources).flat();
}
